import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { TopBar } from '../../components';
import { MigrationAppliancesTable } from './MigrationAppliancesTable';
import { PRIMARY_BUTTONS_CONFIG, SECONDARY_BUTTONS_CONFIG, CLOUD_RESOURCE_NOUNS } from './actions';
import { filterGroupRowKeys } from '../../lib/helpers';
import BulkAddTagsModal from '../../components/modals/BulkAddTagsModal';
import CloudAccountModal from '../../components/modals/CloudAccountModal';
import { ROUTE_CONFIG } from '../../constants/routes';
import { ApplianceSetupModal } from './ApplianceSetupModal';
import { DOC_TYPES } from '../../constants/docLinks';

export const Clouds = () => {
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [addTagsModalOpen, setAddTagsModalOpen] = useState(false);
    const [selectedAppliancesForTags, setSelectedAppliancesForTags] = useState([]);
    const [cloudAccountModalOpen, setCloudAccountModalOpen] = useState(false);
    const [editingCloudAccountId, setEditingCloudAccountId] = useState(null);
    const bulkDeleteTriggerRef = useRef(null);
    const bulkAddTagsTriggerRef = useRef(null);
    const editCloudAccountTriggerRef = useRef(null);
    const redeployApplianceTriggerRef = useRef(null);
    const [applianceSetupModalOpen, setApplianceSetupModalOpen] = useState(false);
    const [setupCloudAccountId, setSetupCloudAccountId] = useState(null);
    const [isRedeploy, setIsRedeploy] = useState(false);

    const userOrg = sessionStorage.getItem('userOrg');

    // Filter out group keys to get only actual appliance IDs
    const selectedApplianceIds = useMemo(() => filterGroupRowKeys(selectedRowKeys), [selectedRowKeys]);
    const selectedCount = selectedApplianceIds.length;

    const handleBulkDelete = useCallback(() => {
        if (selectedCount > 0 && bulkDeleteTriggerRef.current) {
            bulkDeleteTriggerRef.current(selectedApplianceIds);
        }
    }, [selectedApplianceIds, selectedCount]);

    const handleBulkAddTags = useCallback(() => {
        if (selectedCount > 0) {
            setSelectedAppliancesForTags(selectedApplianceIds);
            setAddTagsModalOpen(true);
        }
    }, [selectedApplianceIds, selectedCount]);

    const handleBulkActionSuccess = useCallback(() => {
        // Clear selected rows after successful bulk action
        setSelectedRowKeys([]);
    }, []);

    const handleAddTagsModalClose = useCallback(() => {
        setAddTagsModalOpen(false);
        setSelectedAppliancesForTags([]);
    }, []);

    const handleAddCloudAccount = useCallback(() => {
        setEditingCloudAccountId(null);
        setCloudAccountModalOpen(true);
    }, []);

    const handleEditCloudAccount = useCallback((cloudAccountId) => {
        setEditingCloudAccountId(cloudAccountId);
        setCloudAccountModalOpen(true);
    }, []);

    const handleCloudAccountModalClose = useCallback(() => {
        setCloudAccountModalOpen(false);
        setEditingCloudAccountId(null);
    }, []);

    const handleCloudAccountSuccess = useCallback(
        (cloudAccount, setupAppliance) => {
            if (setupAppliance && cloudAccount && !editingCloudAccountId) {
                // Open appliance setup modal
                setCloudAccountModalOpen(false);
                setSetupCloudAccountId(cloudAccount.id);
                setIsRedeploy(false);
                setApplianceSetupModalOpen(true);
            }
        },
        [editingCloudAccountId],
    );

    const handleRedeployAppliance = useCallback((cloudAccountId) => {
        setSetupCloudAccountId(cloudAccountId);
        setIsRedeploy(true);
        setApplianceSetupModalOpen(true);
    }, []);

    const handleApplianceSetupModalClose = useCallback(() => {
        setApplianceSetupModalOpen(false);
        setSetupCloudAccountId(null);
        setIsRedeploy(false);
    }, []);

    useEffect(() => {
        editCloudAccountTriggerRef.current = handleEditCloudAccount;
    }, [handleEditCloudAccount]);

    useEffect(() => {
        redeployApplianceTriggerRef.current = handleRedeployAppliance;
    }, [handleRedeployAppliance]);

    const actions = useMemo(
        () => ({
            addCloudAccount: handleAddCloudAccount,
            addTags: handleBulkAddTags,
            delete: handleBulkDelete,
        }),
        [handleAddCloudAccount, handleBulkAddTags, handleBulkDelete],
    );

    return (
        <div className="migration-appliances">
            <TopBar
                breadcrumbItems={[{ title: 'Manage' }, { title: ROUTE_CONFIG.clouds.title }]}
                primaryButtons={PRIMARY_BUTTONS_CONFIG}
                secondaryButtons={SECONDARY_BUTTONS_CONFIG}
                actions={actions}
                selectedCount={selectedCount}
                resourceNouns={CLOUD_RESOURCE_NOUNS}
                documentationType={DOC_TYPES.MIGRATION}
            />

            <MigrationAppliancesTable
                selectedRowKeys={selectedRowKeys}
                onSelectedRowKeysChange={setSelectedRowKeys}
                bulkDeleteTriggerRef={bulkDeleteTriggerRef}
                bulkAddTagsTriggerRef={bulkAddTagsTriggerRef}
                editCloudAccountTriggerRef={editCloudAccountTriggerRef}
                redeployApplianceTriggerRef={redeployApplianceTriggerRef}
                onBulkActionSuccess={handleBulkActionSuccess}
            />

            <BulkAddTagsModal
                open={addTagsModalOpen}
                onClose={handleAddTagsModalClose}
                selectedResources={selectedAppliancesForTags}
                userOrg={userOrg}
                onSuccess={handleBulkActionSuccess}
                title="Add Tags to Selected Migration Appliance(s)"
                resourceType="migration appliance"
                apiConfig={{
                    getResourceEndpoint: (resourceId) => `/api/v3/cloudaccounts/${resourceId}`,
                    updateResourceEndpoint: (resourceId) => `/api/v3/cloudaccounts/${resourceId}`,
                }}
            />

            <CloudAccountModal
                open={cloudAccountModalOpen}
                onClose={handleCloudAccountModalClose}
                onSuccess={handleCloudAccountSuccess}
                cloudAccountId={editingCloudAccountId}
                userOrg={userOrg}
            />

            <ApplianceSetupModal
                open={applianceSetupModalOpen}
                onClose={handleApplianceSetupModalClose}
                cloudAccountId={setupCloudAccountId}
                isRedeploy={isRedeploy}
            />
        </div>
    );
};
